import { z } from 'zod'
import {
  notifyAdminActionSchema,
  smsActionSchema,
  transferActionSchema,
} from './actionsSchemas'

// =============================================================================
// Schéma Zod du formulaire AI Agent — source de vérité pour la validation
// et le typage (FormValues = z.infer<typeof schema>).
// =============================================================================

const dayHoursSchema = z.object({
  enabled: z.boolean(),
  start: z.string(),
  end: z.string(),
})

const workingHoursSchema = z.object({
  monday: dayHoursSchema,
  tuesday: dayHoursSchema,
  wednesday: dayHoursSchema,
  thursday: dayHoursSchema,
  friday: dayHoursSchema,
  saturday: dayHoursSchema,
  sunday: dayHoursSchema,
})

export const formSchema = z.object({
  // — Base
  name: z.string().trim().min(1, 'name_required'),
  language: z.enum(['fr', 'en', 'es', 'de', 'fr-en', 'en-fr', 'multi']),
  voice_id: z.string().min(1, 'voice_required'),
  // Expressive mode: our house voices. '' = none picked (the default voice).
  mode_expressif: z.boolean(),
  voix_expressive: z.string(),
  call_mode: z.enum(['inbound', 'outbound']),
  // '' = aucun choix explicite ; 'main' = numéro principal ; 'none' = libérer ;
  // chaîne numérique = id de PhoneNumber.
  phone_number_id: z.string(),
  // Numéro expéditeur SMS pour les agents sans numéro dédié (routés via flow IVR).
  // '' = pas configuré (les SMS de l'agent ne seront pas délivrés).
  sms_from_number: z.string(),

  // — Behavior
  first_message: z.string(),
  system_prompt: z.string().trim().min(1, 'system_prompt_required'),

  // — Knowledge
  business_context: z.string(),
  knowledge_web_urls: z.array(z.object({ url: z.string().url('invalid_url') })),
  knowledge_text: z.string(),

  // — Actions (3 listes)
  sms_actions: z.array(smsActionSchema),
  transfer_actions: z.array(transferActionSchema),
  notify_admin_actions: z.array(notifyAdminActionSchema),

  // — Calendar booking
  google_calendar: z.number().nullable(),
  microsoft_calendar: z.number().nullable(),
  square_booking: z.number().nullable(),
  wix_booking: z.number().nullable(),
  calendar_booking_duration: z.number().int().min(5).default(30),
  calendar_buffer_time: z.number().int().min(0).default(0),
  calendar_default_appointment_type: z.enum(['phone', 'in_person', 'video']),
  calendar_allow_client_type_choice: z.boolean(),
  calendar_intake_info: z.string(),
  // Wix only: the confirmation text carries a link to pay the service's deposit.
  calendar_deposit_link: z.boolean(),
  calendar_working_hours: workingHoursSchema,
  calendar_confirmation_method: z.enum(['sms', 'email', 'both', 'none']),
  calendar_notify_company: z.boolean(),
  calendar_company_notification_method: z.enum(['sms', 'email', 'both']),
  calendar_company_notification_email: z.string(),
  calendar_company_notification_phone: z.string(),
  can_find_appointments: z.boolean(),
  can_cancel_appointments: z.boolean(),
  can_reschedule_appointments: z.boolean(),
  calendar_cancellation_email_subject: z.string(),
  calendar_cancellation_email_body: z.string(),
  calendar_reschedule_email_subject: z.string(),
  calendar_reschedule_email_body: z.string(),
  calendar_reschedule_sms_template: z.string(),
  calendar_sms_template: z.string(),
})

export type FormValues = z.infer<typeof formSchema>
